import { Linkable } from '@/components/blocks/Linkable';
import { quickLinkDynamicSX, quickLinkSX } from '@/components/content/Header/styles/account/panel';
import DynamicIcon, { IconName } from '@/components/icons/DynamicIcon';
import { useSessionDialog } from '@/data/Content/SessionDialog';
import { useLocalization } from '@/data/Localization';
import { toCamelCase } from '@/utils/toCamelCase';
import { Stack, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useSettings } from '@/data/Settings';
import { isCheckoutProfileHidden } from '@/data/config/checkoutProfileToggle';

type Props = {
	theme: Theme;
	isLoggedIn: boolean;
	onClose?: () => void;
};

export const QuickLinksSection: React.FC<Props> = ({ theme, isLoggedIn, onClose }) => {
	const { openSessionDialog } = useSessionDialog();
	const RouteLocal = useLocalization('Routes');
	const HeaderAccountPanelLabels = useLocalization('HeaderAccountPanel');
	const { settings } = useSettings();
	const isCheckoutProfilesHidden = isCheckoutProfileHidden(settings?.userData);

	const openLoginModal = (event: MouseEvent) => {
		event.preventDefault();
		openSessionDialog('signIn');
		onClose?.();
	};
	const quickLinks = [
		{
			icon: HeaderAccountPanelLabels.QuickLinks.Account.Icon.t(),
			title: HeaderAccountPanelLabels.QuickLinks.Account.Title.t(),
			description: HeaderAccountPanelLabels.QuickLinks.Account.Description.t(),
			url: RouteLocal.Account.route,
			className: 'account-panel__pro-login',
		},
		{
			icon: HeaderAccountPanelLabels.QuickLinks.MyOrders.Icon.t(),
			title: HeaderAccountPanelLabels.QuickLinks.MyOrders.Title.t(),
			description: HeaderAccountPanelLabels.QuickLinks.MyOrders.Description.t(),
			url: RouteLocal.OrderHistory.route,
			className: 'account-panel__orders',
		},
		{
			icon: HeaderAccountPanelLabels.QuickLinks.MyAddress.Icon.t(),
			title: HeaderAccountPanelLabels.QuickLinks.MyAddress.Title.t(),
			description: HeaderAccountPanelLabels.QuickLinks.MyAddress.Description.t(),
			url: RouteLocal.AddressBook.route,
			className: 'account-panel__addresses',
		},
		{
			icon: HeaderAccountPanelLabels.QuickLinks.MyPayMethod.Icon.t(),
			title: HeaderAccountPanelLabels.QuickLinks.MyPayMethod.Title.t(),
			description: HeaderAccountPanelLabels.QuickLinks.MyPayMethod.Description.t(),
			url: RouteLocal.PaymentMethod.route,
			className: 'account-panel__payment-methods',
		},
		{
			icon: HeaderAccountPanelLabels.QuickLinks.MyBuyProfile.Icon.t(),
			title: HeaderAccountPanelLabels.QuickLinks.MyBuyProfile.Title.t(),
			description: HeaderAccountPanelLabels.QuickLinks.MyBuyProfile.Description.t(),
			url: RouteLocal.CheckoutProfiles.route,
			className: 'account-panel__checkout-profile',
			isHidden: isCheckoutProfilesHidden,
		},
		{
			icon: HeaderAccountPanelLabels.QuickLinks.MyFavorite.Icon.t(),
			title: HeaderAccountPanelLabels.QuickLinks.MyFavorite.Title.t(),
			description: HeaderAccountPanelLabels.QuickLinks.MyFavorite.Description.t(),
			url: RouteLocal.WishLists.route,
			className: 'account-panel__wishlist',
		},
	];

	return (
		<Stack sx={quickLinkSX.container}>
			{quickLinks.map((item, index) => {
				const linkProps = isLoggedIn
					? { href: item.url.t() }
					: { href: '/', onClick: openLoginModal };

				const isHidden = item.isHidden;

				if (isHidden) return null;

				return (
					<Stack key={item.title + index} sx={quickLinkDynamicSX.itemContainer(theme)}>
						<Linkable sx={quickLinkSX.itemLink} {...linkProps} className={item.className}>
							<DynamicIcon
								name={toCamelCase(item.icon) as IconName}
								aria-label={item.title}
								sx={quickLinkSX.itemIcon}
								size={'24px'}
							/>
							<Stack sx={quickLinkSX.itemContent}>
								<Typography variant="bodyBaseline" sx={quickLinkSX.itemTitle}>
									{item.title}
								</Typography>
								<Typography variant="bodySubText" sx={quickLinkSX.itemDescription}>
									{item.description}
								</Typography>
							</Stack>
						</Linkable>
					</Stack>
				);
			})}
		</Stack>
	);
};
